import React, { useMemo } from 'react';
import { useEFormSubmissions } from '../../e-forms/useData';
import { StatusBreakdown } from './shared';
import type { StatusBreakdownRow } from './shared';

function parseDefinitionId(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (raw && typeof raw === 'object' && 'definitionId' in raw) {
      const nested = parseDefinitionId((raw as { definitionId?: unknown }).definitionId);
      if (nested != null) return nested;
    }
  }
  return null;
}

const EformsSubmissionStatusChart: React.FC<{
  embedded?: boolean;
  definitionId?: number | string;
  settings?: Record<string, unknown>;
}> = ({ embedded, definitionId: definitionIdProp, settings }) => {
  const definitionId = parseDefinitionId(definitionIdProp, settings);
  const { data, isLoading } = useEFormSubmissions(definitionId, undefined, 1, 200);

  const rows: StatusBreakdownRow[] = useMemo(() => {
    let draft = 0;
    let submitted = 0;
    for (const row of data ?? []) {
      if (row.status === 'SUBMITTED') submitted += 1;
      else draft += 1;
    }
    return [
      { key: 'draft', label: 'Draft', value: draft, color: 'bg-slate-500' },
      { key: 'submitted', label: 'Submitted', value: submitted, color: 'bg-emerald-500' },
    ];
  }, [data]);

  return (
    <StatusBreakdown
      title="E-Forms · Status mix"
      rows={rows}
      isLoading={isLoading}
      embedded={embedded}
      viewAllPath={definitionId ? `/e-forms/${definitionId}/submissions` : '/e-forms/submissions'}
      emptyMessage={definitionId ? 'No submissions for this form yet' : 'Choose a form to see status mix'}
    />
  );
};

export default EformsSubmissionStatusChart;
